import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ShowcaseItem } from 'cityads-v3/types/showcases';
import { makeRequest } from 'cityads-v3/utils/apiUtils';
import { requestWrapper } from 'cityads-v3/utils/apiUtils_ts';

export interface ShowcaseResponse {
  showcase: ShowcaseItem
}

export const initialState = {
  editForm: {} as Partial<ShowcaseItem>,
  showcaseIsLoading: false,
  showcaseLoadingError: false,
  showcaseIsSaving: false,
  showcaseIsDeleting: false,
}

export const normalizeShowcaseEntity = ({ showcase }: ShowcaseResponse): ShowcaseItem => {
  return {
    ...showcase,
    categories: [...showcase.categories].map(id => +id),
    recommended_offers: [...showcase.recommended_offers].map(id => +id),
    traffic_types: [...showcase.traffic_types].map(id => +id),
    offers: [...showcase.offers].map(id => +id),
    id: +showcase.id,
  }
}

export const slice = createSlice({
  name: 'showcases',
  initialState,
  reducers: {
    saveShowcaseItem: state => {
      state.showcaseIsSaving = true;
      state.showcaseLoadingError = false;
    },
    saveShowcaseItemSuccess: (state, action: PayloadAction<ShowcaseResponse>) => {
      state.editForm = normalizeShowcaseEntity(action.payload);
      state.showcaseIsSaving = false;
    },
    saveShowcaseItemFail: (state, action) => {
      state.showcaseIsSaving = false;
    },
    loadShowcaseItem: state => {
      state.showcaseIsLoading = true;
    },
    loadShowcaseItemSuccess: (state, action: PayloadAction<ShowcaseResponse>) => {
      state.editForm = normalizeShowcaseEntity(action.payload);
      state.showcaseIsLoading = false;
      state.showcaseLoadingError = false;
    },
    loadShowcaseItemFail: state => {
      state.showcaseIsLoading = false;
      state.showcaseLoadingError = true;
    },
    deleteShowcaseItem: state => {
      state.showcaseIsDeleting = true;
    },
    deleteShowcaseItemSuccess: state => {
      state.showcaseIsDeleting = false;
    },
    deleteShowcaseItemFail: state => {
      state.showcaseIsDeleting = false;
    },
  }
})

const {
  saveShowcaseItem,
  saveShowcaseItemSuccess,
  saveShowcaseItemFail,
  loadShowcaseItem,
  loadShowcaseItemSuccess,
  loadShowcaseItemFail,
  deleteShowcaseItem,
  deleteShowcaseItemSuccess,
  deleteShowcaseItemFail,
} = slice.actions

/**
 * Создание новой витрины
 */
export function createShowcase() {
  return requestWrapper<ShowcaseResponse, ShowcaseItem>(makeRequest(`/saduka/showcases`, {
    types: [
      saveShowcaseItem.type,
      saveShowcaseItemSuccess.type,
      saveShowcaseItemFail.type
    ],
    options: {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        showcase: {}
      })
    }
  }), normalizeShowcaseEntity)
}

/**
 * Сохранение изменений по витрине
 */
export function editShowcase(showcase: Partial<ShowcaseItem>) {
  return requestWrapper<ShowcaseResponse, ShowcaseItem>(makeRequest(`/saduka/showcases/${showcase.id}`, {
    types: [
      saveShowcaseItem.type,
      saveShowcaseItemSuccess.type,
      saveShowcaseItemFail.type
    ],
    options: {
      credentials: 'include',
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        showcase
      })
    }
  }), normalizeShowcaseEntity)
}

/**
 * Загрузка витрины по ID
 */
export function loadShowcaseById(id: number) {
  return requestWrapper<ShowcaseResponse, ShowcaseItem>(makeRequest(`/saduka/showcases/${id}`, {
    types: [
      loadShowcaseItem.type,
      loadShowcaseItemSuccess.type,
      loadShowcaseItemFail.type
    ],
    options: {
      credentials: 'include',
    }
  }), normalizeShowcaseEntity)
}

/**
 * Удаление витрины
 */
export function deleteShowcase(id: number) {
  return makeRequest(`/saduka/showcases/${id}`, {
    types: [
      deleteShowcaseItem.type,
      deleteShowcaseItemSuccess.type,
      deleteShowcaseItemFail.type
    ],
    options: {
      credentials: 'include',
      method: 'DELETE'
    }
  });
}

export default slice.reducer;
